import { track } from '@verfi/shared/dist/analytics/browser';
import React, { useState, useEffect, useRef } from 'react';
import { apiService, Session, NativeProofData, ProofAccess, WebProofEvidence, SessionEvent } from '../services/api';

interface UnlockModalProps {
  verfiID: string;
  session?: Session | null;
  onUnlock: (proof?: NativeProofData, reportedIP?: string | null, access?: ProofAccess, evidence?: WebProofEvidence) => void;
  onClose?: () => void;
}

interface UnlockResponse {
  session?: Session;
  events?: SessionEvent[];
  access?: ProofAccess;
  reportedIP?: string | null;
  proof?: NativeProofData;
  success: boolean;
  error?: string;
}

const UnlockModal: React.FC<UnlockModalProps> = ({ verfiID, session, onUnlock, onClose }) => {
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate days until expiration
  const calculateDaysUntilExpiration = (): string => {
    if (!session?.expirationDate) {
      return 'Unknown expiration';
    }
    
    const expirationDate = new Date(session.expirationDate);
    const currentDate = new Date();
    const timeDiff = expirationDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff <= 0) {
      return 'Expired';
    } else if (daysDiff === 1) {
      return 'Expires in 1 day';
    } else {
      return `Expires in ${daysDiff} days`;
    }
  };

  const handleUnlock = async () => {
    if (isLoading) return;
    if (!email.trim() && !phone.trim()) {
      setError('Please enter either email or phone number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Send plain text credentials to API (API will handle hashing)
      const response: UnlockResponse = await apiService.verifyUnlockCredentials(verfiID, {
        email: email.trim().toLowerCase() || '',
        phone: phone.trim() || ''
      });

      if (!active.current) return;
      if (response.success) {
        // Call onUnlock callback
        track('proof_unlocked'); onUnlock(response.proof, response.reportedIP, response.access, response.session && Array.isArray(response.events) ? { session: response.session, events: response.events } : undefined);
      } else {
        track('operation_failed', { operation: 'unlock', outcome: 'failure' });
        // Handle different types of failures
        if (response.error === 'SESSION_EXPIRED') {
          setError('This session has expired and can no longer be accessed.');
        } else if (response.error === 'SESSION_NOT_FOUND') {
          setError('Session not found. Please check the URL and try again.');
        } else if (response.error === 'INVALID_CREDENTIALS') {
          setError('Invalid credentials. Please check your email or phone number.');
        } else {
          setError('Unable to verify credentials. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Unlock request failed');
      
      track('operation_failed', { operation: 'unlock', outcome: 'failure' });
      const status = err.response?.status ?? err.status;
      // Handle different types of network/API errors
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (status === 404) {
        setError('Session not found. Please check the URL and try again.');
      } else if (status === 401) {
        setError('Email or phone did not match, or contact data is unavailable.');
      } else if (status === 429) {
        setError('Too many attempts. Please try again in 15 minutes.');
      } else if (status === 400) {
        setError('Invalid request. Please check your input and try again.');
      } else if (status === 500) {
        setError('Server error. Please try again later.');
      } else if (status >= 500) {
        setError('Service temporarily unavailable. Please try again later.');
      } else {
        setError('Failed to verify credentials. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleUnlock();
    }
  };

  return (
    React.createElement('div', { className: 'unlock-modal-overlay' },
      React.createElement('div', { className: 'unlock-modal' },
        React.createElement('div', { className: 'unlock-modal-header' },
          React.createElement('h2', null, 'Restricted Access'),
          React.createElement('p', null, 'Enter the email or phone number from this session to access the Proof of Consent.'),
          session?.expirationDate && React.createElement('div', { className: 'unlock-expiration' },
            React.createElement('span', { className: 'expiration-text' }, calculateDaysUntilExpiration())
          )
        ),
        React.createElement('div', { className: 'unlock-modal-body' },
          React.createElement('div', { className: 'unlock-form' },
            React.createElement('div', { className: 'unlock-field' },
              React.createElement('label', { htmlFor: 'email' }, 'Email Address'),
              React.createElement('input', {
                id: 'email',
                type: 'email',
                value: email,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value),
                onKeyPress: handleKeyPress,
                placeholder: 'Enter your email address',
                disabled: isLoading
              })
            ),
            React.createElement('div', { className: 'unlock-divider' },
              React.createElement('span', null, 'OR')
            ),
            React.createElement('div', { className: 'unlock-field' },
              React.createElement('label', { htmlFor: 'phone' }, 'Phone Number'),
              React.createElement('input', {
                id: 'phone',
                type: 'tel',
                value: phone,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value),
                onKeyPress: handleKeyPress,
                placeholder: 'Enter your phone number',
                disabled: isLoading
              })
            ),
            error && React.createElement('div', { className: 'unlock-error' }, error),
            React.createElement('div', { className: 'unlock-actions' },
              React.createElement('button', {
                className: 'unlock-button',
                onClick: handleUnlock,
                disabled: isLoading || (!email.trim() && !phone.trim())
              }, isLoading ? 'Verifying...' : 'Unlock Session'),
              onClose && React.createElement('button', {
                className: 'unlock-cancel-button',
                onClick: onClose,
                disabled: isLoading
              }, 'Cancel')
            )
          )
        ),
        React.createElement('div', { className: 'unlock-modal-footer' },
          React.createElement('p', { className: 'unlock-privacy-note' },
            'Access is restricted to parties who can verify the contact information. This verification protects the privacy of this session.'
          )
        )
      )
    )
  );
};

export default UnlockModal;
